import { RELATED_CONTENT_ACTION_SUBJECT } from '../analytics.ts';

// The Teamwork Graph surface these related-content operational events are emitted from.
const RELATED_CONTENT_SOURCE = 'teamworkGraphPreviewPanel';

/** Category of a related-content fetch failure. */
export type RelatedContentErrorType = 'network' | 'graphql';

/**
 * Fired once when the related-content fetch fires — all prerequisites met and not held by
 * the caller. Pairs with the succeeded/failed events to form the fetch funnel.
 */
export const getRelatedContentRequestedEvent = (): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'requested';
		readonly actionSubject: 'relatedContent';
		readonly source: 'teamworkGraphPreviewPanel';
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'requested',
			actionSubject: RELATED_CONTENT_ACTION_SUBJECT,
			source: RELATED_CONTENT_SOURCE,
		},
	}) as const;

/**
 * Fired once after the graph fetch and ORS hydration both settle without error.
 * `resolvedCount` is the links the section renders; `totalCount` is the raw graph rows
 * returned, so the gap measures ORS resolution drop-off.
 */
export const getRelatedContentSucceededEvent = (attributes: {
	resolvedCount: number;
	totalCount: number;
}): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'succeeded';
		readonly actionSubject: 'relatedContent';
		readonly source: 'teamworkGraphPreviewPanel';
		readonly attributes: { resolvedCount: number; totalCount: number };
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'succeeded',
			actionSubject: RELATED_CONTENT_ACTION_SUBJECT,
			source: RELATED_CONTENT_SOURCE,
			attributes,
		},
	}) as const;

/** Fired when the related-content graph query errors (network or GraphQL). */
export const getRelatedContentFailedEvent = (attributes: {
	errorType: RelatedContentErrorType;
}): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'failed';
		readonly actionSubject: 'relatedContent';
		readonly source: 'teamworkGraphPreviewPanel';
		readonly attributes: { errorType: RelatedContentErrorType };
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'failed',
			actionSubject: RELATED_CONTENT_ACTION_SUBJECT,
			source: RELATED_CONTENT_SOURCE,
			attributes,
		},
	}) as const;
